'use client';

import React, { useMemo } from 'react';
import { useGetProfileTabDataQuery } from 'src/api/redux/profiles';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { formatCurrency } from 'src/utils/formatCurrency/formatCurrency';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import PreviewInlineBar from 'src/components/Profiles/shared/widgets/shared/PreviewInlineBar';

const PREVIEW_MAX_ITEMS = 2;

const CompactOpenPaymentsPreview: React.FC = () => {
  const { entityDetails } = useProfile();

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  const { data, isLoading } = useGetProfileTabDataQuery(
    {
      id: providerId,
      type: profileType,
      view: 'payments',
    },
    { skip: !providerId }
  );

  const items = useMemo(() => {
    const stack = data?.charts?.stack;
    if (!stack?.series?.length) return [];

    const mapped = stack.series.map(
      (series: { name: string; data: number[] }) => ({
        label: series.name,
        value: series.data.reduce(
          (sum: number, v: number) => sum + Number(v),
          0
        ),
      })
    );

    return mapped
      .filter((d: { value: number }) => d.value > 0)
      .sort(
        (a: { value: number }, b: { value: number }) => b.value - a.value
      )
      .slice(0, PREVIEW_MAX_ITEMS);
  }, [data]);

  if (isLoading) {
    return <WidgetLoading data-testid="compact-open-payments-preview-loading" aria-label="Loading open payments" />;
  }

  if (items.length === 0) {
    return <WidgetEmpty data-testid="compact-open-payments-preview-empty" />;
  }

  const maxValue = items[0].value;

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="compact-open-payments-preview"
    >
      {items.map((item: { label: string; value: number }) => (
        <PreviewInlineBar
          key={item.label}
          label={item.label}
          value={item.value}
          maxValue={maxValue}
          formattedValue={formatCurrency(item.value)}
        />
      ))}
    </div>
  );
};

export default CompactOpenPaymentsPreview;
